/**
 * Passkey Service - WebAuthn/Passkey API wrapper
 *
 * Handles all passkey-related operations including registration,
 * listing, renaming, and deletion.
 */

import api from './api'
import { startRegistration, startAuthentication } from '@simplewebauthn/browser'

const MAX_PASSKEYS = 5

const passkeyService = {
  /**
   * List all passkeys for the current user
   * @returns {Promise<{passkeys: Array, count: number, maxAllowed: number, canAddMore: boolean}>}
   */
  async listPasskeys() {
    const response = await api.get('/passkeys')
    return response.data
  },

  /**
   * Register a new passkey
   * @param {string} name - Optional name for the passkey
   * @returns {Promise<{success: boolean, passkey: object}>}
   */
  async registerPasskey(name = null) {
    // Generate a default name if not provided
    const passkeyName = name || `Passkey ${new Date().toLocaleDateString()}`

    // Step 1: Get registration options from server
    const optionsResponse = await api.post('/passkeys/register/options', {
      name: passkeyName
    })

    if (!optionsResponse.data) {
      throw new Error('Failed to get registration options')
    }

    // Step 2: Trigger WebAuthn registration in browser
    // This prompts the user for biometric/PIN verification
    const registrationResponse = await startRegistration(optionsResponse.data)

    // Step 3: Send response to server for verification
    const verifyResponse = await api.post('/passkeys/register/verify', {
      ...registrationResponse,
      name: passkeyName
    })

    return verifyResponse.data
  },

  /**
   * Rename an existing passkey
   * @param {string} id - Passkey ID
   * @param {string} name - New name for the passkey
   * @returns {Promise<{success: boolean, passkey: object}>}
   */
  async renamePasskey(id, name) {
    const response = await api.put(`/passkeys/${id}`, { name })
    return response.data
  },

  /**
   * Delete a passkey
   * @param {string} id - Passkey ID
   * @returns {Promise<{success: boolean}>}
   */
  async deletePasskey(id) {
    const response = await api.delete(`/passkeys/${id}`)
    return response.data
  },

  /**
   * Check if the user can add more passkeys
   * @param {number} currentCount - Current number of passkeys
   * @returns {boolean}
   */
  canAddMore(currentCount) {
    return currentCount < MAX_PASSKEYS
  },

  /**
   * Check if WebAuthn is supported by the browser
   * @returns {boolean}
   */
  isSupported() {
    return (
      window.PublicKeyCredential !== undefined && typeof window.PublicKeyCredential === 'function'
    )
  },

  /**
   * Check if a platform authenticator (Face ID, Touch ID, Windows Hello) is available
   * @returns {Promise<boolean>}
   */
  async isPlatformAuthenticatorAvailable() {
    if (!this.isSupported()) return false
    try {
      return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()
    } catch {
      return false
    }
  },

  /**
   * Get the maximum number of passkeys allowed
   * @returns {number}
   */
  getMaxPasskeys() {
    return MAX_PASSKEYS
  },

  // ============================================================================
  // PASSKEY AUTHENTICATION (Login)
  // ============================================================================

  /**
   * Check if Conditional UI (autofill) is available
   * @returns {Promise<boolean>}
   */
  async isConditionalUIAvailable() {
    if (!this.isSupported()) return false
    try {
      return (await PublicKeyCredential.isConditionalMediationAvailable?.()) || false
    } catch {
      return false
    }
  },

  /**
   * Start passkey authentication with Conditional UI (autofill)
   * Call this on Login page mount to enable passkey autofill
   * @returns {Promise<{success: boolean, user: object, token: string}>}
   */
  async authenticateWithConditionalUI() {
    // Get authentication options from server
    const optionsResponse = await api.post('/passkeys/login/options')
    const { challengeId, ...options } = optionsResponse.data

    // Start authentication with conditional mediation (autofill)
    const authResponse = await startAuthentication({
      optionsJSON: options,
      useBrowserAutofill: true
    })

    // Verify with server
    const verifyResponse = await api.post('/passkeys/login/verify', {
      challengeId,
      ...authResponse
    })

    return verifyResponse.data
  },

  /**
   * Start passkey authentication (explicit, non-autofill)
   * @returns {Promise<{success: boolean, user: object, token: string}>}
   */
  async authenticate() {
    // Get authentication options from server
    const optionsResponse = await api.post('/passkeys/login/options')
    const { challengeId, ...options } = optionsResponse.data

    // Start authentication
    const authResponse = await startAuthentication({
      optionsJSON: options
    })

    // Verify with server
    const verifyResponse = await api.post('/passkeys/login/verify', {
      challengeId,
      ...authResponse
    })

    return verifyResponse.data
  }
}

export default passkeyService
